import { sortBy } from "lodash";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

const QuestionSetSelector = ({
  assessmentType,
  availableAssessmentGroupSelectOptions,
  canUpdate,
  onUpdateAssessmentTemplate,
  onDuplicateAssessmentGroup,
  onOpenAssessmentGroupSidebar,
  selectedAssessmentGroup,
  error,
}: {
  assessmentType: AssessmentType;
  availableAssessmentGroupSelectOptions: ComboboxGenericOption<number>[];
  canUpdate: boolean;
  onUpdateAssessmentTemplate: (assessmentGroupId: number | null) => void;
  onDuplicateAssessmentGroup: (
    assessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onOpenAssessmentGroupSidebar: () => void;
  selectedAssessmentGroup: AssessmentGroupListFragmentFragment | null;
  error?: string;
}) => {
  return (
    <div className="flex flex-col gap-1 scroll-mt-24">
      <div
        className={classNames(
          "flex items-center gap-3 p-4 rounded-sm border",
          error ? "border-red-300 bg-red-50" : "bg-white",
        )}
      >
        <ComboboxGeneric
          placeholder="Select a question set"
          containerClassName="flex-1"
          aria-label={`Question set selector: ${assessmentType}`}
          options={sortBy(availableAssessmentGroupSelectOptions, (opt) =>
            opt.label.toLocaleLowerCase(),
          )}
          disabled={!canUpdate}
          onChangeValue={(opt) => onUpdateAssessmentTemplate(opt.value)}
          createLabel="Create New Question Set"
          onClickCreateNew={() => {
            onUpdateAssessmentTemplate(null);
            onOpenAssessmentGroupSidebar();
          }}
          value={
            selectedAssessmentGroup
              ? {
                  value: selectedAssessmentGroup.id,
                  label: selectedAssessmentGroup.title,
                }
              : null
          }
        />
        <Button
          disabled={!selectedAssessmentGroup}
          theme={buttonTheme.iconGray}
          icon={TopicflowIconMap.edit}
          onClick={onOpenAssessmentGroupSidebar}
        />
        <Button
          disabled={!selectedAssessmentGroup || !canUpdate}
          icon={TopicflowIconMap.copy}
          theme={buttonTheme.iconGray}
          onClick={() => {
            onDuplicateAssessmentGroup(assertNonNull(selectedAssessmentGroup));
            onOpenAssessmentGroupSidebar();
          }}
        />
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
};

export default QuestionSetSelector;
